import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { dt, showError } from "./util";

const sel = "h-9 w-full rounded-lg border border-input bg-white px-2 text-sm";

export function RestoreDialog({ backup, onClose, onDone }) {
  const [f, setF] = useState({ password: "", passphrase: "" });
  const [kit, setKit] = useState(null);
  const [info, setInfo] = useState(null);
  const [scope, setScope] = useState("PLATFORM");
  const [office, setOffice] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setF({ password: "", passphrase: "" }); setKit(null); setInfo(null); setConfirm(""); setOffice(""); }, [backup]);

  const form = (extra = {}) => {
    const fd = new FormData();
    fd.append("password", f.password); fd.append("passphrase", f.passphrase);
    if (kit) fd.append("kit", kit);
    Object.entries(extra).forEach(([k, v]) => v && fd.append(k, v));
    return fd;
  };
  const verify = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/backups/${backup.id}/verify`, form());
      setInfo(r.data); setScope(r.data.scope); setOffice(r.data.office_id || "");
    } catch (e) { await showError(e); }
    setBusy(false);
  };
  const restore = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/backups/${backup.id}/restore`, form({ scope, office_id: scope === "OFFICE" ? office : "", confirm }));
      toast.success(`Geri yükleme tamamlandı: ${r.data.records} kayıt, ${r.data.files} dosya`);
      onDone(); onClose();
    } catch (e) { await showError(e); }
    setBusy(false);
  };

  return (
    <Dialog open={!!backup} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="restore-dialog">
        <DialogHeader>
          <DialogTitle>Yedeği doğrula ve geri yükle</DialogTitle>
          <DialogDescription>{backup?.filename} · {dt(backup?.created_at)}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className="block"><span className="text-xs font-semibold text-slate-600">Hesap şifreniz</span>
            <Input type="password" data-testid="restore-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></label>
          <label className="block"><span className="text-xs font-semibold text-slate-600">Kurtarma parolası</span>
            <Input type="password" data-testid="restore-passphrase" value={f.passphrase} onChange={(e) => setF({ ...f, passphrase: e.target.value })} /></label>
          <label className="block text-xs text-slate-600">Kurtarma anahtarı dosyası (yalnızca sunucu kaybı sonrası veya eski anahtarla alınmış yedekler için)
            <input type="file" accept=".emlakplus-key" data-testid="restore-kit-file" className="block mt-1 text-xs" onChange={(e) => setKit(e.target.files?.[0] || null)} /></label>
          {!info && <Button className="w-full" variant="outline" data-testid="restore-verify-btn" disabled={busy || !f.password || !f.passphrase} onClick={verify}>
            <ShieldCheck className="w-4 h-4 mr-1.5" />{busy ? "Doğrulanıyor..." : "Bütünlüğü ve şifreyi doğrula"}</Button>}
          {info && (
            <div className="space-y-3" data-testid="restore-verified">
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm">
                <div className="font-semibold text-emerald-800">Geçerli EmlakPlus yedeği · bütünlük doğrulandı</div>
                <div className="text-xs text-emerald-900 mt-1">{info.scope === "PLATFORM" ? "Tüm platform" : "Tek ofis"} · {info.records} kayıt · {info.files} belge · {info.offices.length} ofis · {dt(info.created_at)}</div>
              </div>
              <label className="block"><span className="text-xs font-semibold text-slate-600">Geri yükleme kapsamı</span>
                <select className={sel} data-testid="restore-scope" value={scope} onChange={(e) => setScope(e.target.value)}>
                  {info.scope === "PLATFORM" && <option value="PLATFORM">Tüm platform (mevcut tüm veriler değiştirilir)</option>}
                  <option value="OFFICE">Tek ofis (diğer ofislere dokunulmaz)</option>
                </select></label>
              {scope === "OFFICE" && <label className="block"><span className="text-xs font-semibold text-slate-600">Ofis</span>
                <select className={sel} data-testid="restore-office" value={office} onChange={(e) => setOffice(e.target.value)}>
                  <option value="">Seçin</option>
                  {info.offices.map((o) => <option key={o.id} value={o.id}>{o.name} ({o.office_code})</option>)}
                </select></label>}
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2">Geri yüklemeden önce mevcut durumun şifreli bir yedeği otomatik alınır. Onaylamak için <b>GERİ YÜKLE</b> yazın.</p>
              <Input data-testid="restore-confirm" placeholder="GERİ YÜKLE" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
              <Button className="w-full bg-red-600 hover:bg-red-700" data-testid="restore-submit-btn" disabled={busy || confirm.trim().toLocaleUpperCase("tr-TR") !== "GERİ YÜKLE" || (scope === "OFFICE" && !office)} onClick={restore}>
                {busy ? "Geri yükleniyor..." : "Geri yükle"}</Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
